import React, { memo, useEffect, useRef, useState } from 'react'
import CheckIcon from '@mui/icons-material/Check'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, Link, Typography } from '@mui/material'
import { AxiosError } from 'axios'
import { isPinRequestLimitExceeded } from './utils'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { getStorage } from 'utils/storage'
import { LocalStorage } from 'utils/storage/localStorage'

const DEV_CONFIG_KEY = LocalStorage.DEV_CONFIG
const getTimers = () => {
  try {
    const raw = getStorage('localStorage').getItem(DEV_CONFIG_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.isShortResendTimersEnabled) {
        return { RESEND_TIME: 5, RESEND_COOLDOWN: 2 }
      }
    }
  } catch {}
  return { RESEND_TIME: 30, RESEND_COOLDOWN: 10 }
}
const { RESEND_TIME, RESEND_COOLDOWN } = getTimers()

const PIN_RESEND_OPTIONS = [
  { method: 'sms', label: 'Neue PIN per SMS senden' },
  { method: 'email', label: 'Neue PIN per E-Mail senden' }
]

interface ResendPinProps {
  setPin: (pin: string) => void
  isMobileHidden: boolean
}

const ResendPin: React.FC<ResendPinProps> = ({ setPin, isMobileHidden }) => {
  const {
    prefill: { getPrefillPin },
    traversal: {
      traversalId,
      data: {
        system: { advertisementId }
      }
    }
  } = useStores()
  const isPinLimitReached = isPinRequestLimitExceeded()
  const [resendTimeLeft, setResendTimeLeft] = useState(RESEND_TIME)
  const [isResent, setIsResent] = useState(false)
  const [isResendPinLoading, setIsResendPinLoading] = useState(false)
  const [isMaxAttemptsReached, setIsMaxPinRequestsReached] =
    useState(isPinLimitReached)
  const [isResendError, setIsResendError] = useState(false)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (resendTimeLeft > 0 && !isResent) {
      intervalRef.current = setInterval(() => {
        setResendTimeLeft((prev) => prev - 1)
      }, 1000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [resendTimeLeft, isResent])

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleResendPin = async (method: 'sms' | 'email') => {
    trackInAmplitude(AmplitudeEvent.PrefillResendPinClicked, {
      method
    })

    setIsResendPinLoading(true)
    setIsResendError(false)
    setIsResent(false)
    setPin('')

    try {
      await getPrefillPin(advertisementId, traversalId, method)
      trackInAmplitude(AmplitudeEvent.PrefillResendPinSuccess, {
        method
      })
      setIsResent(true)
    } catch (err) {
      const errorObj = err as AxiosError
      const status: number | undefined = errorObj?.status

      trackInAmplitude(AmplitudeEvent.PrefillResendPinFailed, {
        method,
        status
      })

      switch (status) {
        case 429:
          setIsMaxPinRequestsReached(true)
          break
        case 500:
          setIsResendError(true)
          break
        default:
          setIsResendError(true)
          break
      }

      logAndTrackError(
        new Error(`/prefill/pin/send failed. Error status: ${status}`)
      )
    }

    setIsResendPinLoading(false)

    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
      if (isPinRequestLimitExceeded()) {
        setIsMaxPinRequestsReached(true)
      }
      setIsResendError(false)
      setIsResent(false)
      setResendTimeLeft(RESEND_TIME)
      timeoutRef.current = null
    }, RESEND_COOLDOWN * 1000)
  }

  useEffect(() => {
    if (isMaxAttemptsReached) {
      trackInAmplitude(AmplitudeEvent.PrefillMaxAttemptsReachedErrorShown)
    }
  }, [isMaxAttemptsReached])

  useEffect(() => {
    if (isResent && !isResendError && !isMaxAttemptsReached) {
      trackInAmplitude(AmplitudeEvent.PrefillIsResentPinMessageShown)
    }
  }, [isResent, isMaxAttemptsReached, isResendError])

  const availableResendOptions = PIN_RESEND_OPTIONS.filter(
    (option) => !(isMobileHidden && option.method === 'sms')
  )

  return (
    <Box>
      {resendTimeLeft > 0 &&
        !isResent &&
        !isResendError &&
        !isMaxAttemptsReached && (
          <Typography
            variant="body2"
            color={(theme) => theme.palette.grey[600]}
          >
            Neue PIN in {resendTimeLeft} Sekunden senden
          </Typography>
        )}

      {resendTimeLeft === 0 &&
        !isResent &&
        !isResendError &&
        !isMaxAttemptsReached && (
          <div>
            {availableResendOptions.map(({ method, label }) => (
              <Box key={method} mt={method === 'sms' ? 0 : 2}>
                <Link
                  onClick={() => {
                    if (isResendPinLoading) return
                    handleResendPin(method as 'sms' | 'email')
                  }}
                  underline="none"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: isResendPinLoading ? 'progress' : 'pointer',
                    color: isResendPinLoading ? 'grey.600' : 'primary.main'
                  }}
                >
                  <Typography fontWeight={700}>{label}</Typography>
                  {!isResendPinLoading && <ChevronRightIcon color="primary" />}
                </Link>
              </Box>
            ))}
          </div>
        )}

      {isResent && !isResendError && !isMaxAttemptsReached && (
        <Box display="flex" alignItems="center" gap={0.5}>
          <CheckIcon color="primary" fontSize="small" />
          <Typography variant="body2">Gesendet!</Typography>
        </Box>
      )}

      {!isResent && isResendError && !isMaxAttemptsReached && (
        <Box display="flex" alignItems="flex-start" gap={0.5}>
          <WarningAmberIcon color="warning" />
          <Typography variant="body2">
            Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später
            erneut.
          </Typography>
        </Box>
      )}

      {isMaxAttemptsReached && (
        <Box display="flex" alignItems="flex-start" gap={0.5}>
          <WarningAmberIcon color="warning" />
          <Typography variant="body2">
            Sie haben die maximal erlaubte Anzahl von PINs angefordert.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default memo(ResendPin)
